import React, { useState, ReactElement } from 'react';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { idvPurchaseEnabled } from '../config/Master';
import ConfirmVerificationModal from './modals/verification/ConfirmVerificationModal';
import { mixpanelTrackEvent } from '../utils/mixpanel/common';
import { EVENT_NAMES } from '../constants/mixpanel';
import { IAccount } from '../types/common';
import { RootState } from '../store';
import IDVButtonV2 from '../atoms/buttons/IDVButtonV2';
import { isDarkMode } from 'utils/helpers';

const InfoItemDescription = styled('div')(() => ({
  fontSize: 'inherit',
  width: '100%',
}));

const getSideBarStyle = () => ({
  margin: '0 auto',
  minWidth: 'unset',
  fontSize: 15,
  borderRadius: '4px !important',
  fontStyle: 'italic',
  padding: '23px 10px',
});

interface PurchaseIDVProps {
  account: IAccount;
  sideBar?: boolean;
  customIDVerificationStatusStyle?: React.CSSProperties;
  isProfile?: boolean;
}

const IDVVerification = (props: PurchaseIDVProps): ReactElement => {
  const navigate = useNavigate();

  const { account, sideBar = false } = props;

  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const { status } = useSelector((state: RootState) => state.profile.id_verification.data);

  const onContinueIDV = () => navigate('/verification');

  const renderBtnIDV = () => {
    if (!account) return;

    const isPending = status === 'pending';
    const isRequired = status === 'required';
    const isPaidStatus = status === 'paid';
    const isNotStarted = status === '' || status === 'not_started';
    const isDenied = status === 'denied';
    const showContinueBtn = !isPending && (isRequired || isPaidStatus);

    const customSideStyle = sideBar ? getSideBarStyle() : {};

    if (sideBar && isDenied) {
      return (
        <IDVButtonV2
          customStyle={{
            ...customSideStyle,
          }}
          status={'unsuccessful'}
          buttonContent={
            <>
              <InfoItemDescription
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: isDarkMode() ? '#F08EA4' : '#DD0B24',
                  fontStyle: 'italic',
                }}
              >
                ID Verification Failed
              </InfoItemDescription>
            </>
          }
        />
      );
    }

    if (isNotStarted) {
      return (
        <IDVButtonV2
          data-test-id={sideBar ? 'sidebar-buy-idv' : 'btn-buy-idv'}
          customStyle={{
            ...customSideStyle,
            fontStyle: 'normal',
          }}
          onClick={() => {
            document.body.style.overflow = 'hidden';
            setShowConfirmModal(true);
            mixpanelTrackEvent({
              eventName: EVENT_NAMES.ID_VERIFICATION_POPUP_OPENED,
              payload: {},
            });
          }}
          status={'getIdVerified'}
          buttonContent={
            <>
              <span style={{ fontWeight: 400, color: 'inherit' }}>Get</span>&nbsp;ID Verified
            </>
          }
        />
      );
    }

    if (showContinueBtn) {
      return (
        <IDVButtonV2
          customStyle={{
            ...customSideStyle,
          }}
          onClick={onContinueIDV}
          status={'idvNotComplete'}
          buttonContent={
            <>
              <InfoItemDescription style={{ lineHeight: 1, color: 'inherit', fontStyle: 'italic', fontWeight: 400 }}>
                IDV: <span style={{ color: 'inherit', fontWeight: 600 }}>Not Complete</span>
                <br />
                <span style={{ color: 'inherit', fontSize: 8, textDecoration: 'underline' }}>(Tap to try again)</span>
              </InfoItemDescription>
            </>
          }
        />
      );
    }

    if (isPending) {
      return (
        <IDVButtonV2
          customStyle={{
            ...customSideStyle,
          }}
          status={'pending'}
          buttonContent={
            <>
              <InfoItemDescription style={{ color: 'inherit', fontStyle: 'italic', fontWeight: 400 }}>
                IDV Pending...
              </InfoItemDescription>
            </>
          }
        />
      );
    }

    return (
      <IDVButtonV2
        customStyle={{
          ...customSideStyle,
        }}
        status="idVerified"
        buttonContent={
          <>
            <div style={{ fontWeight: 600, fontStyle: 'italic' }}>ID Verified!</div>
          </>
        }
      />
    );
  };

  return (
    <React.Fragment>
      {showConfirmModal && (
        <ConfirmVerificationModal
          onClose={() => {
            document.body.style.overflow = 'unset';
            setShowConfirmModal(false);
          }}
          contentLabel={'Confirm ID Verification'}
          isOpen={showConfirmModal}
          onConfirm={() => {
            setShowConfirmModal(false);
            mixpanelTrackEvent({
              eventName: EVENT_NAMES.BUY_VERIFICATION_PAGE_VIEW,
              payload: {},
            });

            navigate('/idv-packages?step=payment&package_id=idv_last_minute');
          }}
        />
      )}
      {idvPurchaseEnabled && renderBtnIDV()}
    </React.Fragment>
  );
};

export default IDVVerification;
